// Sales orders register (BUILD_SPEC.md §5.7, §8.4 step 1's optional
// sales-order link). Added to close the audit's #1 functional blocker:
// services/api/src/modules/sales/sales-orders.ts had a complete DRAFT->OPEN->
// COMPLETE/CANCELLED backend and zero screens — plant staff could not create,
// view, edit, open, complete, or cancel a sales order. Mirrors
// DispatchesRegister.tsx's cursor-pagination register idiom.
// Columns are limited to what GET /sales/sales-orders REALLY returns — raw
// rows, NO `lines` (see api.ts's `SalesOrderRow`; 2026-07-10 wave-gate
// BLOCKER: a `so.lines.length` column crashed on any real row). Line detail
// lives on the detail page, which fetches the serialized by-id shape.
import { useState } from 'react';
import { Button, Group, Select, Stack, Table } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconClipboardList, IconPlus } from '@tabler/icons-react';
import { SALES_ORDER_STATUSES, type SalesOrderStatus } from '@zebra/core';
import { useSalesOrdersList } from './api';
import { formatDate } from './format';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { CustomerName } from './components/EntityNames';

const NEW_SALES_ORDER_BUTTON = (
  <Button component={Link} to="/sales-invoicing/sales-orders/new" leftSection={<IconPlus size={16} />}>
    New sales order
  </Button>
);

export function SalesOrdersRegister() {
  const [status, setStatus] = useState<SalesOrderStatus | undefined>(undefined);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const { data, isLoading } = useSalesOrdersList({ cursor, status });
  const rows = data?.items ?? [];
  // Header/EmptyState CTA de-dupe (guidance-wave gap, 2026-08-26): on an empty
  // register the EmptyState below already offers "New sales order" as the sole
  // call-to-action — showing the same button in the page header too rendered
  // TWO "New sales order" buttons on a fresh tenant (Playwright strict-mode
  // failure). Reuses the exact emptiness condition the EmptyState branch checks.
  const showHeaderCreate = isLoading || rows.length > 0;

  return (
    <Stack gap="md">
      <PageHeader
        title="Sales orders"
        description="Record what a customer wants so it can be dispatched — in one load or several — and invoiced afterwards. First step in sales order → dispatch → invoice → credit note."
        actions={
          <Group gap="sm">
            <Select
              placeholder="All statuses"
              clearable
              data={SALES_ORDER_STATUSES.map((s) => ({ value: s, label: s }))}
              value={status ?? null}
              onChange={(v) => {
                setStatus((v as SalesOrderStatus) ?? undefined);
                setCursors([undefined]);
              }}
              w={160}
            />
            {showHeaderCreate && NEW_SALES_ORDER_BUTTON}
          </Group>
        }
      />

      <Table.ScrollContainer minWidth={640}>
        <Table striped highlightOnHover verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Number</Table.Th>
              <Table.Th>Customer</Table.Th>
              <Table.Th>Reference</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((so) => (
              <Table.Tr key={so.id}>
                <Table.Td>
                  <Link to="/sales-invoicing/sales-orders/$salesOrderId" params={{ salesOrderId: so.id }}>
                    {so.number}
                  </Link>
                </Table.Td>
                <Table.Td>
                  <CustomerName id={so.customerId} />
                </Table.Td>
                <Table.Td>{so.reference ?? '—'}</Table.Td>
                <Table.Td>
                  <DocStatusBadge status={so.status} domain="salesOrder" />
                </Table.Td>
                <Table.Td>{formatDate(so.createdAt)}</Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <EmptyState
                    icon={IconClipboardList}
                    title="No sales orders yet"
                    hint="Record a customer's order to plan what needs dispatching — dispatches can also be posted without one."
                    action={NEW_SALES_ORDER_BUTTON}
                  />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {data?.nextCursor && (
        <Group justify="center">
          <Button variant="subtle" onClick={() => setCursors((prev) => [...prev, data.nextCursor ?? undefined])}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}
